import { useState } from 'react';
import { Link } from 'react-router-dom';
import CartButton from '../../components/feature/CartButton';

export default function CustomOrderPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    productType: '',
    quantity: '',
    budget: '',
    deliveryDate: '',
    deliveryAddress: '',
    giftMessage: '',
    specialRequest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // 필수 항목 검증
    if (!formData.name || !formData.phone || !formData.email || !formData.productType || !formData.quantity) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }

    // 메시지 길이 검증
    if (formData.giftMessage.length > 500) {
      alert('선물 메시지는 500자를 초과할 수 없습니다.');
      return;
    }

    if (formData.specialRequest.length > 500) {
      alert('특별 요청사항은 500자를 초과할 수 없습니다.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d60bd1dcojuehkk4llpg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          productType: '',
          quantity: '',
          budget: '',
          deliveryDate: '',
          deliveryAddress: '',
          giftMessage: '',
          specialRequest: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 네비게이션 */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-12">
            <Link to="/">
              <img src="https://static.readdy.ai/image/f77122aa20e12351d50b895437d7646f/26f7e4287336b6a14c4caa48b3ce1ec3.png" alt="초이담 참새한과" className="h-12 w-auto cursor-pointer" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" onClick={() => setTimeout(() => document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' }), 100)} className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer">홈</Link>
              <Link to="/" onClick={() => setTimeout(() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' }), 100)} className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer">브랜드 소개</Link>
              <Link 
                to="/custom-order" 
                className="text-sm font-medium text-amber-700 transition-colors cursor-pointer"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                맞춤 선물
              </Link>
              <Link to="/" onClick={() => setTimeout(() => document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' }), 100)} className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer">메뉴</Link>
              <Link to="/" onClick={() => setTimeout(() => document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth' }), 100)} className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer">고객 후기</Link>
              <Link to="/" onClick={() => setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 100)} className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer">문의</Link>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CartButton isScrolled={true} />
          </div>
        </div>
      </nav>

      {/* 히어로 섹션 */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-100 px-5 py-2 rounded-full mb-8">
            <i className="ri-gift-line text-amber-700 text-sm"></i>
            <span className="text-xs font-medium text-amber-900">맞춤 선물 주문</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            특별한 날을 위한<br />맞춤 선물 주문
          </h1>
          
          <p className="text-sm text-gray-600 mb-12 leading-relaxed">
            명절, 기념일, 특별한 날을 위한 맞춤 한과 선물을 준비해드립니다.<br />
            원하시는 구성과 수량으로 정성스럽게 제작해드립니다.
          </p>
        </div>
      </section>

      {/* 주문 프로세스 */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: "'Noto Serif KR', serif" }}>주문 프로세스</h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-amber-900">1</span>
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">주문서 작성</h3>
              <p className="text-xs text-gray-600">원하시는 상품과 수량을 선택해주세요</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-amber-900">2</span>
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">상담 진행</h3>
              <p className="text-xs text-gray-600">담당자가 연락드려 상세 상담을 진행합니다</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-amber-900">3</span>
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">제작 시작</h3>
              <p className="text-xs text-gray-600">주문 확정 후 신선하게 제작합니다</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-amber-900">4</span>
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">배송 완료</h3>
              <p className="text-xs text-gray-600">정성스럽게 포장하여 배송해드립니다</p>
            </div>
          </div>
        </div>
      </section>

      {/* 주문 폼 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-8">
          <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8" style={{ fontFamily: "'Noto Serif KR', serif" }}>맞춤 선물 주문서</h2>
            
            <form id="custom-order-form" onSubmit={handleSubmit} data-readdy-form>
              {/* 주문자 정보 */}
              <div className="mb-10">
                <h3 className="text-base font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <i className="ri-user-line text-amber-700"></i>
                  주문자 정보
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      이름 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="홍길동"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      연락처 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="010-1234-5678"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      이메일 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="example@email.com"
                    />
                  </div>
                </div>
              </div>

              {/* 상품 정보 */}
              <div className="mb-10">
                <h3 className="text-base font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <i className="ri-shopping-bag-line text-amber-700"></i>
                  상품 정보
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      상품 종류 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="productType"
                      value={formData.productType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="">선택해주세요</option>
                      <option value="약과">약과</option>
                      <option value="유과">유과</option>
                      <option value="강정">강정</option>
                      <option value="다식">다식</option>
                      <option value="정과">정과</option>
                      <option value="선물세트">선물 세트</option>
                      <option value="맞춤구성">맞춤 구성</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      수량 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="예: 10세트"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      예산 범위
                    </label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="">선택해주세요</option>
                      <option value="3만원 이하">3만원 이하</option>
                      <option value="3-5만원">3만원 - 5만원</option>
                      <option value="5-10만원">5만원 - 10만원</option>
                      <option value="10-20만원">10만원 - 20만원</option>
                      <option value="20만원 이상">20만원 이상</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* 배송 정보 */}
              <div className="mb-10">
                <h3 className="text-base font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <i className="ri-truck-line text-amber-700"></i>
                  배송 정보
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      희망 배송일
                    </label>
                    <input
                      type="date"
                      name="deliveryDate"
                      value={formData.deliveryDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      배송지 주소
                    </label>
                    <input
                      type="text"
                      name="deliveryAddress"
                      value={formData.deliveryAddress}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="서울시 강남구 테헤란로 123"
                    />
                  </div>
                </div>
              </div>

              {/* 추가 정보 */}
              <div className="mb-10">
                <h3 className="text-base font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <i className="ri-message-3-line text-amber-700"></i>
                  추가 정보
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      선물 메시지 (최대 500자)
                    </label>
                    <textarea
                      name="giftMessage"
                      value={formData.giftMessage}
                      onChange={handleInputChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm resize-none"
                      placeholder="선물과 함께 전달할 메시지를 입력해주세요"
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {formData.giftMessage.length} / 500자
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      특별 요청사항 (최대 500자)
                    </label>
                    <textarea
                      name="specialRequest"
                      value={formData.specialRequest}
                      onChange={handleInputChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-sm resize-none"
                      placeholder="포장 방법, 알레르기 정보 등 특별히 요청하실 사항이 있으시면 입력해주세요"
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {formData.specialRequest.length} / 500자
                    </div>
                  </div>
                </div>
              </div>

              {/* 안내 사항 */}
              <div className="bg-amber-50 rounded-xl p-6 mb-8">
                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-amber-700"></i>
                  주문 안내
                </h4>
                <ul className="space-y-2 text-xs text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-amber-700 mt-0.5"></i>
                    <span>주문서 제출 후 1-2일 내에 담당자가 연락드립니다.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-amber-700 mt-0.5"></i>
                    <span>최소 주문 수량은 상품에 따라 다를 수 있습니다.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-amber-700 mt-0.5"></i>
                    <span>제작 기간은 주문 확정 후 3-5일 소요됩니다.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-amber-700 mt-0.5"></i>
                    <span>명절 시즌에는 제작 기간이 더 소요될 수 있습니다.</span>
                  </li>
                </ul>
              </div>

              {/* 제출 상태 메시지 */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-green-600 text-lg"></i>
                    <div>
                      <p className="text-sm font-medium text-green-900">주문서가 성공적으로 제출되었습니다!</p>
                      <p className="text-xs text-green-700 mt-1">담당자가 곧 연락드리겠습니다.</p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <i className="ri-error-warning-fill text-red-600 text-lg"></i>
                    <div>
                      <p className="text-sm font-medium text-red-900">제출 중 오류가 발생했습니다.</p>
                      <p className="text-xs text-red-700 mt-1">다시 시도해주시거나 전화로 문의해주세요.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* 제출 버튼 */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gray-900 text-white text-sm rounded-full font-medium hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '제출 중...' : '주문서 제출하기'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* 문의 섹션 */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            궁금하신 점이 있으신가요?
          </h2>
          <p className="text-sm text-gray-600 mb-8">
            전화나 카카오톡으로 편하게 문의해주세요
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-amber-900 text-lg"></i>
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">전화 문의</h3>
              <p className="text-gray-600 text-xs">02-1234-5678</p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-chat-3-line text-amber-900 text-lg"></i>
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">카카오톡</h3>
              <p className="text-gray-600 text-xs">@초이담한과</p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-amber-900 text-lg"></i>
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">상담 시간</h3>
              <p className="text-gray-600 text-xs">평일 09:00 - 18:00</p>
            </div>
          </div>
        </div>
      </section>

      {/* 푸터 */}
      <footer className="bg-gradient-to-b from-amber-900 to-amber-950 text-white mx-5 mb-5 rounded-t-3xl">
        <div className="max-w-7xl mx-auto px-12 py-16">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-1">
              <h3 className="text-2xl font-bold leading-tight mb-6" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                초이담과 함께<br />특별한 선물을
              </h3>
            </div>

            <div>
              <h4 className="text-sm font-medium text-white mb-6">메뉴</h4>
              <ul className="space-y-4 text-xs text-amber-100">
                <li><Link to="/#home" className="hover:text-white transition-colors cursor-pointer">홈</Link></li>
                <li><Link to="/#about" className="hover:text-white transition-colors cursor-pointer">브랜드 소개</Link></li>
                <li><Link to="/custom-order" className="hover:text-white transition-colors cursor-pointer">맞춤 선물</Link></li>
                <li><Link to="/#menu" className="hover:text-white transition-colors cursor-pointer">메뉴</Link></li>
                <li><Link to="/#contact" className="hover:text-white transition-colors cursor-pointer">오시는 길</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-medium text-white mb-6">소셜 미디어</h4>
              <ul className="space-y-4 text-xs text-amber-100">
                <li className="flex items-center gap-3 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-base"></i>
                  <span>Instagram</span>
                </li>
                <li className="flex items-center gap-3 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-article-line text-base"></i>
                  <span>Blog</span>
                </li>
                <li className="flex items-center gap-3 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-chat-3-line text-base"></i>
                  <span>KakaoTalk</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-medium text-white mb-3">소식 받아보기</h4>
              <p className="text-xs text-amber-100 mb-4">새로운 메뉴와 이벤트 소식을 받아보세요</p>
            </div>
          </div>

          <div className="relative pt-8 border-t border-amber-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-xs text-amber-200">© 2024 초이담 참새한과. All rights reserved.</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-amber-200 hover:text-white transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}